import React, { useCallback, useEffect, useMemo } from "react";
import { useMeetingAPIs } from "../../../../hooks/meeting";

import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { MeetingPlayerTriLayoutContainer, MeetingPlayerTriLayoutContainerProps } from "../../../../components/player/container/MeetingPlayerTriLayoutContainer";
import { PlayerItem } from "../../../../components/player/interfaces";
import { MasterPlayerWorkBox } from "../../../../components/workBoxes/MasterPlayerWorkBox";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useSdkContext } from "../../../../contexts/sdk/context";
import { isExpert } from "../../../../helpers/roles";
import { useBoardMedia } from "../../../../hooks/board";
import { pushDeviceControlsCmd } from "../../../../hooks/deviceControls";
import { useVideoSubscriptions } from "../../../../hooks/ovenmedia";
import "./dashboard.css";
import styles from "./dashboard.module.css";
import { useMeetingSelector } from "@kalyzee/kast-web-sdk";

export const MeetingDashboardStudentPage = () => {
  const sdkCtx = useSdkContext();

  const { selectedContainerAPI } = useMeetingAPIs();

  const [meeting] = useMeetingSelector(selectedContainerAPI, "meeting");
  const [sessions] = useMeetingSelector(selectedContainerAPI, "sessions");
  const [ownSession] = useMeetingSelector(selectedContainerAPI, "session");

  const layout = useAppStoreSelector(state => state.layout);
  const meetingOptions = useAppStoreSelector(state => state.meetingOptions);

  const subscribptionsAPI = useVideoSubscriptions();

  const boardLocalOrRemoteMedia = useBoardMedia(selectedContainerAPI);

  // Enabled : enabled videos in the left drawer
  const { enabledMedia } = useMemo(() => {
    if (!meeting) return { enabledMedia: [] };
    const studentMeetingOptions = meetingOptions[meeting.id];

    const enabledVideoSessionsIds = studentMeetingOptions?.disabledVideoSessionIds;
    const enabledMedia = boardLocalOrRemoteMedia.filter((media) => !enabledVideoSessionsIds?.includes(media.mediaSession.id));

    return { enabledMedia };
  }, [meeting?.id, boardLocalOrRemoteMedia, meetingOptions]);

  const playerItems = useMemo(() => enabledMedia?.map(
    (media) =>
      new PlayerItem(media.type === "local" ? {
        type: "stream",
        mediaStream: media.stream,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      } : {
        type: "video_session",
        videoSession: media.videoSession,
        extra: {
          meetingSession: media.session,
          mediaSession: media.mediaSession,
        },
      })
  ), [enabledMedia]);

  const renderPlayer = useCallback((
    rendererProvider: () => React.ReactNode,
    session: MeetingSession,
    mediaSession: MeetingSessionMedia,
    isSmall: boolean,
    onSelect?: () => void,
  ) => {
    if (!meeting || !selectedContainerAPI) return null;

    const hasControls = !!mediaSession.extra?.controls && isExpert(ownSession);
    const deviceId = mediaSession.deviceId;
    const roomId = meeting.room?.id;
    return (
      <MasterPlayerWorkBox
        meetingAPI={selectedContainerAPI}
        session={session}
        mediaSession={mediaSession}
        overlayMode={"overlay"}
        overlayContent={
          onSelect 
            ? { type: "selection", onSelect } 
            : hasControls 
              ? {
                type: "controls",
                onUp: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "move" : "stopMove", { direction: "up" });
                },
                onDown: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "move" : "stopMove", { direction: "down" });
                },
                onLeft: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "move" : "stopMove", { direction: "left" });
                },
                onRight: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "move" : "stopMove", { direction: "right" });
                },
                onZoomIn: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "zoom" : "stopZoom", { direction: "in" });
                },
                onZoomOut: (on: boolean) => {
                  if (!roomId || !deviceId) return;
                  pushDeviceControlsCmd(sdkCtx.client, roomId, deviceId, on ? "zoom" : "stopZoom", { direction: "out" });
                },
              } 
              : undefined
        }
        isSmall={isSmall}
        mediaControls
      >
        {rendererProvider()}
      </MasterPlayerWorkBox>
    );
  }, [meeting]);

  const triLayoutProps = useMemo((() => ({ square: { noPages: true } })), []);
  const triLayoutRenderItem = useCallback<NonNullable<MeetingPlayerTriLayoutContainerProps["renderItem"]>>((
    rendererProvider,
    { item, isSelectedPlayer, onSelect },
  ) => {
    const itemData = item.getData();
    const session = itemData.extra?.meetingSession;
    const mediaSession = itemData.extra?.mediaSession;
    if (!session || !mediaSession) return undefined;
    return renderPlayer(rendererProvider, session, mediaSession, !isSelectedPlayer, onSelect);
  }, [renderPlayer]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   EFFECT ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    const remoteVideoSessions = enabledMedia
      ?.filter((media) => media.type === "remote")
      .map((media) => (media as any).videoSession);
    subscribptionsAPI.update(remoteVideoSessions.map((media) => ({ session: media, active: true })));
    return () => subscribptionsAPI.update(remoteVideoSessions.map((s) => ({ session: s, active: false })));
  }, [subscribptionsAPI, enabledMedia]);

  // ---------------------------------------------------------------------- //
  // ---------------------------   RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  if (!meeting) return null;
  if (!selectedContainerAPI) return null;

  const renderNewLayoutContent = () => {
    if (!playerItems?.length) {
      return (
        <div style={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed var(--color-element-text)",
          borderRadius: 20,
        }}>
          <div style={{ color: "var(--color-element-text)" }}>{boardLocalOrRemoteMedia.length ? "Aucun flux sélectionné" : "Aucun flux réceptionné"}</div>
        </div>
      );
    }
    return (
      <MeetingPlayerTriLayoutContainer
        meeting={meeting}
        sessions={sessions ?? []}
        items={playerItems}
        layoutProps={triLayoutProps}
        renderItem={triLayoutRenderItem}
        layout={layout}
        autoOnOffVideo={false}
      />
    );
  };

  return (
    <div className={styles.container}>
      {renderNewLayoutContent()}
      {/* {renderFocusContent()} */}
    </div>
  );
};
